import { Link, useLocation } from "@tanstack/react-router";
import {
  BarChart3,
  Briefcase,
  Building,
  Building2,
  CalendarClock,
  CalendarRange,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  Inbox,
  Layers,
  LifeBuoy,
  Plug,
  Radio,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  UserRound,
  Users,
  X,
} from "lucide-react";
import foxLogo from "@/assets/nymbl-fox-blue.png";
import nymblLogoWhite from "@/assets/nymbl-logo-white.png";
import { useSession } from "@/lib/auth-client";
import { canSeeIntegrations, canSeeResourcesPage, visibleSettingsTabs } from "@/lib/nav-access";
import { cn } from "@/lib/utils";
import { useSidebar } from "./SidebarContext";
import { UserMenu } from "./UserMenu";

interface NavItem {
  label: string;
  icon: typeof Layers;
  href: string;
  disabled?: boolean;
  badge?: number;
  badgeTone?: "danger" | "warn" | "info";
  /** Omitted → shown to every signed-in role. The route guard behind it agrees. */
  visibleTo?: (role: string | null | undefined) => boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    label: "Manage",
    items: [
      { label: "Solutions", icon: Layers, href: "/dashboard" },
      { label: "Staffing Requests", icon: Inbox, href: "/dashboard/staffing-requests" },
      { label: "Signals", icon: Radio, href: "/dashboard/signals" },
      { label: "Plans", icon: ClipboardList, href: "/dashboard/plans" },
      {
        label: "Plan Approvals",
        icon: ShieldCheck,
        href: "/dashboard/plans/approvals",
      },
      {
        label: "Resources",
        icon: Users,
        href: "/dashboard/resources",
        visibleTo: canSeeResourcesPage,
      },
      { label: "Schedule", icon: CalendarRange, href: "/dashboard/schedule" },
    ],
  },
  {
    label: "Operations",
    items: [
      { label: "Accounts", icon: Building2, href: "/dashboard/accounts" },
      { label: "Demands", icon: Briefcase, href: "/dashboard/demands" },
    ],
  },
  {
    // Reports (docs/specs/reports-hours.md): the Availability report (#259) and the hours
    // reports beside the existing staffing reports.
    label: "Reports",
    items: [
      { label: "Availability", icon: CalendarClock, href: "/dashboard/availability" },
      { label: "Demand by Account", icon: Building, href: "/dashboard/reports/demand" },
      { label: "Resource Hours", icon: UserRound, href: "/dashboard/reports/resources" },
      { label: "Staffing Reports", icon: BarChart3, href: "/dashboard/reporting" },
    ],
  },
  {
    label: "System",
    items: [
      {
        label: "Configuration",
        icon: SettingsIcon,
        // The settings route sends each role to its first visible tab.
        href: "/dashboard/settings",
        visibleTo: (role) => visibleSettingsTabs(role).length > 0,
      },
      { label: "Logs", icon: ScrollText, href: "/dashboard/logs" },
      { label: "Issues", icon: LifeBuoy, href: "/dashboard/issues" },
      {
        label: "Integrations",
        icon: Plug,
        href: "/dashboard/integrations",
        visibleTo: canSeeIntegrations,
      },
    ],
  },
];

function NavRow({
  item,
  collapsed,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  onClick: () => void;
}) {
  const location = useLocation();
  const isActive =
    item.href === "/dashboard"
      ? location.pathname === "/dashboard"
      : location.pathname.startsWith(item.href);

  const content = (
    <>
      <span className={cn("flex flex-1 items-center gap-2.5", collapsed && "flex-none")}>
        <item.icon className="h-4 w-4 shrink-0 opacity-80" />
        {!collapsed && <span>{item.label}</span>}
      </span>
      {!collapsed && item.badge != null && item.badge > 0 ? (
        <span
          className={cn(
            "rounded-full px-1.5 py-px text-xs font-semibold leading-4",
            item.badgeTone === "danger" && "bg-[var(--fit-at-risk)] text-white",
            item.badgeTone === "warn" && "bg-warning text-warning-950",
            item.badgeTone === "info" && "bg-white/15 text-white",
            !item.badgeTone && "bg-white/15 text-white",
          )}
        >
          {item.badge}
        </span>
      ) : null}
      {!collapsed && item.disabled && (
        <span className="text-xs uppercase tracking-wider text-white/40">soon</span>
      )}
    </>
  );

  const baseClasses = cn(
    "flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-sm font-medium transition-colors",
    collapsed && "justify-center px-0",
    isActive
      ? "bg-sidebar-accent text-white shadow-[inset_2px_0_0_var(--sidebar-primary)]"
      : "text-white/70 hover:bg-white/8 hover:text-white",
    item.disabled && "pointer-events-none opacity-40",
  );

  if (item.disabled) {
    return (
      <div className={baseClasses} title={collapsed ? item.label : undefined}>
        {content}
      </div>
    );
  }
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={baseClasses}
      title={collapsed ? item.label : undefined}
    >
      {content}
    </Link>
  );
}

export function Sidebar() {
  const { isMobile, mobileOpen, setMobileOpen, collapsed, toggleCollapsed } = useSidebar();
  // Every page is viewable by every signed-in role (product decision 2026-10-08), except
  // that a Solution Architect does not see the Resources page or a System item it cannot
  // act on (2026-10-20, see src/lib/nav-access.ts). What a role can DO on a page is
  // enforced by the API, and read-only pages say so themselves.
  const { data: session } = useSession();
  const role = session?.user?.role;
  const sections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.visibleTo === undefined || item.visibleTo(role)),
  })).filter((section) => section.items.length > 0);

  const close = () => isMobile && setMobileOpen(false);
  const isCollapsed = !isMobile && collapsed;

  const content = (
    <aside
      className={cn(
        "nymbl-panel relative flex flex-col overflow-hidden bg-sidebar text-sidebar-foreground",
        isMobile ? "h-full w-56" : "h-full",
      )}
      style={
        isMobile
          ? undefined
          : { width: collapsed ? "var(--sidebar-w-collapsed)" : "var(--sidebar-w)" }
      }
    >
      {/* The nymbl.app hero grid, faded toward the edges. Decorative only. */}
      <div aria-hidden className="nymbl-grid absolute inset-0" />
      <div
        className="relative flex shrink-0 items-center gap-2.5 border-b border-sidebar-border px-3 font-display"
        style={{ height: "var(--topbar-h)" }}
      >
        {isCollapsed ? (
          <img
            src={foxLogo}
            alt="Nymbl"
            className="mx-auto h-5 w-5"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        ) : (
          <>
            <img src={nymblLogoWhite} alt="Nymbl" className="h-6 w-auto" />
            <span className="h-4 w-px bg-white/[0.32]" />
            <span className="text-sm font-medium tracking-[0.2em] text-white/[0.82] uppercase">
              Staff
            </span>
          </>
        )}
        {isMobile && (
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="ml-auto rounded p-1.5 text-white/70 hover:bg-white/10"
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {!isCollapsed && (
        <div className="relative px-2 pb-1 pt-2.5">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/40" />
            <input
              type="search"
              placeholder="Search Solutions, Resources, Accounts…"
              className="h-7 w-full rounded-md border border-white/10 bg-white/10 pl-7 pr-3 text-sm text-white placeholder-white/50 outline-none transition focus:border-white/30 focus:bg-white/15"
            />
          </div>
        </div>
      )}

      <nav className="relative flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section, idx) => (
          <div key={section.label} className={cn(idx > 0 && "mt-4")}>
            {!isCollapsed && (
              <div className="px-2.5 pb-1 font-mono text-xs font-medium uppercase tracking-[0.08em] text-sidebar-primary">
                {section.label}
              </div>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavRow key={item.label} item={item} collapsed={isCollapsed} onClick={close} />
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="relative border-t border-sidebar-border px-2 py-2">
        <UserMenu collapsed={isCollapsed} />
        {!isMobile && (
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
            className={cn(
              "mt-1.5 flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-xs text-white/40 hover:bg-white/10 hover:text-white/70",
              isCollapsed ? "justify-center" : "justify-start",
            )}
          >
            {collapsed ? (
              <ChevronsRight className="h-3.5 w-3.5" />
            ) : (
              <ChevronsLeft className="h-3.5 w-3.5" />
            )}
            {!isCollapsed && <span>Collapse</span>}
          </button>
        )}
      </div>
    </aside>
  );

  if (isMobile) {
    if (!mobileOpen) return null;
    return (
      <>
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileOpen(false)}
        />
        <div className="fixed left-0 top-0 z-50 h-screen">{content}</div>
      </>
    );
  }
  return content;
}
